'use client'

import { chatPrefillMark } from '@/app/lib/guideChat/prefill'
import LoanChatPrefilledBadge from '@/app/components/loanComparator/fields/LoanChatPrefilledBadge'

import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import {
    useLoanComparatorStore,
    useHydration,
    useServerDataLoaded,
    isNewLoan,
} from '@/app/stores/loan/loan-comparator.store'
import LoanComparatorQuotationBorrowerForm, { type BorrowerFormHandle } from './LoanComparatorQuotationBorrowerForm'
import LoanComparatorFinancingForm, { type FinancingFormHandle } from './LoanComparatorFinancingForm'
import LoanComparatorLoanForm, { type LoanFormHandle } from './LoanComparatorLoanForm'
import LoanComparatorBorrowerGuaranteesForm, {
    type BorrowerGuaranteesFormHandle,
} from './LoanComparatorBorrowerGuaranteesForm'
import {
    PROJECT_PURPOSES,
    PROJECT_PURPOSE_RESIDENTIAL,
    PROJECT_PURPOSE_SCPI,
    type ProjectPurposeKey,
    PROJECT_TYPE_NEW_LOAN,
    PROJECT_TYPE_SWITCHING,
    LOAN_STATUS,
    LOAN_INFORMATION_TYPES,
    needsSwitching,
} from '@/app/constants/loanTypes'
import { useTracking } from '@/app/contexts/tracking-context'
import { useFeatureFlag } from '@/app/hooks/useFeatureFlag'
import { useLoanPdfImportState } from '@/app/hooks/useLoanPdfImportState'
import { LoanPdfImportProvider } from '@/app/contexts/loan-pdf-import-context'
import LoanPdfExtractBlock from '@/app/components/loanComparator/pdfExtract/LoanPdfExtractBlock'
import LoanFillModeSelector, {
    type LoanFillMode,
} from '@/app/components/loanComparator/pdfExtract/LoanFillModeSelector'
import LoanFillModeConfirmModal from '@/app/components/loanComparator/pdfExtract/LoanFillModeConfirmModal'
import LoanPdfExtractSkeletons from '@/app/components/loanComparator/pdfExtract/LoanPdfExtractSkeletons'
import { LoanPdfExtractResult, LoanPdfExtractStatus, useLoanPdfExtract } from '@/app/hooks/useLoanPdfExtract'
import LoanImportedBadge from '@/app/components/loanComparator/pdfExtract/LoanImportedBadge'
import LoanPromoStepReminder from '@/app/components/loanComparator/promo/LoanPromoStepReminder'
import { isLoanPromoCodeEnabled } from '@/app/utils/loan/promo-code'
import { PROJECT_PURPOSE_FIELD_KEY, quoteHasLoanInput } from '@/app/utils/loan/loan-pdf-extract-merge'
import { PDF_EXTRACT_LEAVE_WARNING } from '@/app/constants/loanPdfExtract'
import { AeTrackingEvents } from '@/app/constants/loanAeTracking'
import { buildAeParams, buildAeStepParams } from '@/app/utils/loan/loan-ae-tracking'
import DescriptiveToggle, { DescriptiveToggleOption } from '@/app/components/atomic/molecules/DescriptiveToggle'
import LoanFormField from './fields/LoanFormField'
import AppIcon from '@/app/components/atomic/AppIcon'
import ShadowBox from '@/app/components/atomic/ShadowBox'
import AppButtonNew from '@/app/components/atomic/molecules/AppButton'
import {
    getLoanQuoteFromToken,
    postProjectType,
    postQuotationForm,
    updateQuoteStatus,
    type IQuotationFormPayload,
} from '@/app/endpoints/loanComparatorEndpoints'
import { loanHasStages } from '@/app/schemas/loan-comparator/loan.schema'
import { type ILoanFieldValidation } from '@/app/models/loanQuote'
import { mapBorrowerForSubmission } from '@/app/utils/loan/borrower-mapper'
import { mapLoanForSubmission } from '@/app/utils/loan/loan-mapper'
import { scrollToFirstErrorInScreen, goToFirstScreenWithError } from '@/app/utils/loan/loan-screen-errors'
import {
    computeLoansTotalAmount,
    getInsuredAmountError,
    getMinInsuredAmountError,
    hasAltCurrencyLoan,
    resolveLoanBounds,
} from '@/app/utils/loan/loan-insured-amount'
import { scrollWizardToTop } from '@/app/utils/loan/scroll-to-top'
import { timeOffsetFromLoanForm } from '@/app/utils/loanCalculations'
import StepsBreadcrumb from '@/app/components/atomic/StepsBreadcrumb'
import LoanComparatorIdentityForm from './LoanComparatorIdentityForm'
import LoanComparatorProjectStep, { deriveProjectPathValue, type ProjectPathValue } from './LoanComparatorProjectStep'
import LoanComparatorBankCoverageQuestion, {
    isBankCoverageAnswerComplete,
    type BankCoverageAnswer,
} from './LoanComparatorBankCoverageQuestion'
import {
    SCREEN_PROJECT,
    SCREEN_PROFILE_MAIN,
    SCREEN_PROFILE_CO,
    SCREEN_LOAN,
    SCREEN_INSURANCE,
    SCREEN_CONTACT,
    screenToVisibleStep,
    visibleStepToScreen,
    buildLoanQuotationBreadcrumbs,
    QUOTATION_ERROR_SCREENS_ORDER,
    isContactStepReachable,
} from './loan-quotation-steps.constants'
import { useAdsConsentSync } from '@/app/hooks/useAdsConsentSync'

// Maximum number of loans allowed
const MAX_LOANS = 6

// Error message for cumulative coverage validation
const COVERAGE_ERROR_MESSAGE = 'Le taux de couverture cumulé des emprunteurs doit être égal à au moins 100%.'

type InsuredAmountErrors = { perLoan: Record<number, string | null>; main: string | null; co: string | null }

const PROJECT_PURPOSE_DESCRIPTIONS: Record<ProjectPurposeKey, string> = {
    residential: "Vous y habitez à l'année.",
    'residential-secondary': "Vous y séjournez une partie de l'année.",
    rental: 'Vous mettez le bien en location.',
    professional: 'Financement lié à votre activité.',
    scpi: 'Investissement en parts de SCPI.',
}

const PROJECT_PURPOSE_OPTIONS: DescriptiveToggleOption<ProjectPurposeKey>[] = PROJECT_PURPOSES.map((purpose) => ({
    value: purpose.key,
    title: purpose.label,
    description: PROJECT_PURPOSE_DESCRIPTIONS[purpose.key],
    icon: <AppIcon iconClassName={`fa-${purpose.icon}`} size="lg" type="regular" className="text-pale" />,
}))

const projectPurposeId = (key: ProjectPurposeKey): number =>
    PROJECT_PURPOSES.find((purpose) => purpose.key === key)?.id ?? PROJECT_PURPOSE_RESIDENTIAL

// A residential quote reopens on « Résidence principale » : the secondary-home split lives on the client only
const projectPurposeKey = (id: number | undefined): ProjectPurposeKey =>
    PROJECT_PURPOSES.find((purpose) => purpose.id === id)?.key ?? 'residential'

interface LoanComparatorQuotationFormProps {
    initialStep?: number
}

export default function LoanComparatorQuotationForm({ initialStep = 1 }: LoanComparatorQuotationFormProps) {
    const { track } = useTracking()
    const hasHydrated = useHydration()
    const isServerDataLoaded = useServerDataLoaded()
    const quote = useLoanComparatorStore((state) => state.quote)
    useAdsConsentSync(quote?.token, 'loan')
    const uiErrors = useLoanComparatorStore((state) => state.ui.errors)
    // Select project_purpose from type_insurance.id (API returns type_insurance, not project_purpose)
    // This matches VueJs: state.form.project_purpose = quote.type_insurance.id
    const quoteProjectPurpose = useLoanComparatorStore((state) => state.quote?.type_insurance?.id)
    const uiActions = useLoanComparatorStore((state) => state.uiActions)
    const loanActions = useLoanComparatorStore((state) => state.loan)
    const quoteActions = useLoanComparatorStore((state) => state.quoteActions)
    const borrowerActions = useLoanComparatorStore((state) => state.borrower)
    const formOptions = useLoanComparatorStore((state) => state.formOptions)
    const globalOptions = useLoanComparatorStore((state) => state.globalOptions)

    // Refs for child forms
    const mainBorrowerFormRef = useRef<BorrowerFormHandle>(null)
    const coBorrowerFormRef = useRef<BorrowerFormHandle>(null)
    const financingFormRef = useRef<FinancingFormHandle>(null)
    const loanFormsRefs = useRef<Map<number, LoanFormHandle>>(new Map())
    const mainGuaranteesFormRef = useRef<BorrowerGuaranteesFormHandle>(null)
    const coGuaranteesFormRef = useRef<BorrowerGuaranteesFormHandle>(null)

    // Form submission state
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Wizard screen state (écrans internes 1-6 mappés sur 5 étapes visibles)
    const [screen, setScreen] = useState(() => visibleStepToScreen(initialStep))
    const [isNavigating, setIsNavigating] = useState(false)
    // L'étape coordonnées est montée à sa première visite pour que son prefill et
    // IDENTITY_STEP_VISITED se déclenchent au même moment funnel qu'aujourd'hui
    const [hasReachedContact, setHasReachedContact] = useState(
        () => visibleStepToScreen(initialStep) === SCREEN_CONTACT
    )

    const hasCoBorrower = !!quote?.co_borrower?.activated

    const isSubmitScreen = screen === SCREEN_INSURANCE

    // Erreurs des questions hors react-hook-form (étape 1 + question banque de l'étape 3)
    const [projectError, setProjectError] = useState<string | null>(null)
    const [bankCoverageAnswer, setBankCoverageAnswer] = useState<BankCoverageAnswer>(null)
    const [bankCoverageError, setBankCoverageError] = useState<string | null>(null)

    // Incrémenté quand le refresh serveur remplace le quote après un changement de type de projet :
    // re-monte les formulaires enfants pour que RHF se ré-initialise sur le store frais
    // (équivalent du remount pleine page parcours → devis d'avant)
    const [formsEpoch, setFormsEpoch] = useState(0)

    const isPdfImportEnabled = useFeatureFlag('loan_pdf_upload')
    const { value: pdfImportValue, setImportedFields } = useLoanPdfImportState()
    const [isExtracting, setIsExtracting] = useState(false)
    const [fillModeOverride, setFillModeOverride] = useState<LoanFillMode | null>(null)
    const [pendingFillMode, setPendingFillMode] = useState<LoanFillMode | null>(null)
    const [signatureDate, setSignatureDate] = useState('')
    const [isSignatureDateValid, setIsSignatureDateValid] = useState(false)

    const getCurrentQuote = useCallback(() => useLoanComparatorStore.getState().quote, [])
    // Sur un devis sans ligne de prêt, la date de signature ne vit que dans le formulaire
    const signatureDateRef = useRef('')
    const getSignatureDate = useCallback(() => signatureDateRef.current, [])

    const isGuidedLoanStep = isPdfImportEnabled && isNewLoan(quote)
    // Un devis déjà renseigné s'ouvre en saisie manuelle : sinon ses lignes disparaissent derrière l'upload
    const fillMode: LoanFillMode =
        fillModeOverride ?? (isGuidedLoanStep && !quoteHasLoanInput(quote) ? 'import' : 'manual')

    const aeParams = useMemo(() => buildAeParams(quote), [quote])

    const handlePdfExtracted = useCallback(
        ({ quote: merged, imported }: LoanPdfExtractResult) => {
            track?.({
                event: AeTrackingEvents.pdfImportSuccess,
                ...aeParams,
                loans_count: merged.loans?.length ?? 0,
                imported_fields_count: imported.size,
            })
            quoteActions.setQuote(merged)
            setImportedFields(imported)
            uiActions.clearAllErrors()
            setFormsEpoch((epoch) => epoch + 1)
            setFillModeOverride('import')
        },
        [quoteActions, setImportedFields, uiActions, track, aeParams]
    )

    const handlePdfStatusChange = useCallback((status: LoanPdfExtractStatus) => {
        setIsExtracting(status === 'analyzing')
    }, [])

    const handlePdfError = useCallback(
        (code: string) => {
            track?.({ event: AeTrackingEvents.pdfImportError, ...aeParams, error_code: code })
            if (code === 'RATE_LIMIT_EXCEEDED') setFillModeOverride('manual')
        },
        [track, aeParams]
    )

    const pdfExtract = useLoanPdfExtract({
        token: quote?.token,
        getCurrentQuote,
        onExtracted: handlePdfExtracted,
        onStatusChange: handlePdfStatusChange,
        onError: handlePdfError,
        getSignatureDate,
    })

    const isVerifyingImport = pdfExtract.status === 'success'
    const isAwaitingImport = isGuidedLoanStep && fillMode === 'import' && !isVerifyingImport

    const handleFileAccepted = (file: File) => {
        track?.({ event: AeTrackingEvents.pdfImportStart, ...aeParams, file_size_kb: Math.round(file.size / 1024) })
        pdfExtract.run(file)
    }

    const handleHelpOpen = useCallback(() => {
        track?.({ event: AeTrackingEvents.pdfImportHelp, ...aeParams })
    }, [track, aeParams])

    const handleSignatureDateChange = useCallback((date: string, isValid: boolean) => {
        signatureDateRef.current = date
        setSignatureDate(date)
        setIsSignatureDateValid(isValid)
    }, [])

    const applyFillMode = (next: LoanFillMode) => {
        track?.({ event: AeTrackingEvents.pdfImportModeSwitch, ...aeParams, mode: next })
        setPendingFillMode(null)

        if (next === 'manual') {
            if (pdfExtract.status === 'analyzing') pdfExtract.cancel()
            const snapshot = pdfExtract.getSnapshot()
            if (snapshot && pdfImportValue.importedFields.size > 0) {
                quoteActions.setQuote({
                    ...snapshot,
                    loans: snapshot.loans?.map((loan) => ({
                        ...loan,
                        new_date_loan: signatureDate || loan.new_date_loan,
                    })),
                })
                setImportedFields(new Set())
                uiActions.clearAllErrors()
                setFormsEpoch((epoch) => epoch + 1)
            }
            pdfExtract.reset()
        }

        setFillModeOverride(next)
    }

    const requestFillMode = (next: LoanFillMode) => {
        if (next === fillMode) return
        const needsConfirmation =
            next === 'manual' && (pdfImportValue.importedFields.size > 0 || pdfExtract.status === 'analyzing')
        if (needsConfirmation) {
            setPendingFillMode(next)
            return
        }
        applyFillMode(next)
    }

    // RM-903 fait écrire « ne fermez pas cette page » : autant l'empêcher réellement
    useEffect(() => {
        if (!isExtracting) return
        const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = PDF_EXTRACT_LEAVE_WARNING
        }
        window.addEventListener('beforeunload', warnBeforeLeaving)
        return () => window.removeEventListener('beforeunload', warnBeforeLeaving)
    }, [isExtracting])

    const isProjectPurposeImported = pdfImportValue.importedFields.has(PROJECT_PURPOSE_FIELD_KEY)
    const prefilledByChat = useLoanComparatorStore((state) => state.prefilledByChat)
    const loanTypeChatMark = chatPrefillMark(prefilledByChat, 'loanType')

    // Dernier type de projet posté au back : un Suivant sans changement ne re-poste pas
    // (équivalent d'un deep-link direct sur le devis aujourd'hui)
    const postedPathRef = useRef<ProjectPathValue | null | undefined>(undefined)
    useEffect(() => {
        if (!isServerDataLoaded || postedPathRef.current !== undefined) return
        // Un quote legacy « rachat de crédit » est considéré non posté : le premier Suivant nettoie le flag serveur
        postedPathRef.current = quote?.type_quote && !quote.is_loan_repurchase ? deriveProjectPathValue(quote) : null
    }, [isServerDataLoaded, quote])

    // Handoff LP (widget quick start) : le seed serveur écrase Seul/À deux (non persisté avant
    // le POST complet), le param ?borrowers ré-applique la réponse après le seed
    const searchParams = useSearchParams()
    const borrowersHandoffRef = useRef(false)
    useEffect(() => {
        if (!isServerDataLoaded || borrowersHandoffRef.current) return
        borrowersHandoffRef.current = true

        const handoffBorrowers = searchParams.get('borrowers')
        if (handoffBorrowers === 'duo') {
            borrowerActions.addCoBorrower()
        } else if (handoffBorrowers === 'alone') {
            borrowerActions.deactivateCoBorrower()
        }
    }, [isServerDataLoaded, searchParams, borrowerActions])

    // Pre-answered "covered by the bank"; only a known current insurer means "external" (delegation defaults to true server-side)
    useEffect(() => {
        if (!isServerDataLoaded) return
        const isExternal = !!quote?.current_insurance_company
        if (!isExternal) quoteActions.update({ delegation: false })
        // eslint-disable-next-line react-hooks/set-state-in-effect -- valeur initiale posée à l'arrivée des données serveur, modifiable ensuite par l'utilisateur
        setBankCoverageAnswer((current) => (current === null ? (isExternal ? 'external' : 'bank') : current))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isServerDataLoaded])

    const showBankCoverageQuestion = !!quote && !isNewLoan(quote)

    const goToScreen = useCallback((nextScreen: number) => {
        setScreen(nextScreen)
        if (nextScreen === SCREEN_CONTACT) setHasReachedContact(true)
        scrollWizardToTop()
    }, [])

    // Sync de l'URL sur l'étape visible — replaceState comme le wizard santé :
    // router.replace remonterait la page et perdrait l'état des formulaires
    useEffect(() => {
        if (!quote?.token) return
        window.history.replaceState(
            null,
            '',
            `/devis-assurance-pret-immobilier/${quote.token}/step-${screenToVisibleStep(screen)}`
        )
    }, [screen, quote?.token])

    // Sans prêt persisté le devis n'a jamais été soumis : un token seul ouvrirait l'étape coordonnées et créerait un lead vide
    const contactLocked = isServerDataLoaded && !isContactStepReachable(quote)
    useEffect(() => {
        if (!contactLocked || screen !== SCREEN_CONTACT) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- le devis n'est connu qu'après hydratation du store, l'écran demandé ne peut être corrigé qu'ici
        setHasReachedContact(false)
        goToScreen(SCREEN_PROJECT)
    }, [contactLocked, screen, goToScreen])

    // Shared coverage error state - displayed under both quotité fields when co-borrower exists
    const [coverageError, setCoverageErrorState] = useState<string | null>(null)

    // Project purpose state (type de projet) - initialized from quote or default
    const [projectPurpose, setProjectPurpose] = useState<ProjectPurposeKey>(projectPurposeKey(quoteProjectPurpose))

    // Sync project purpose from quote when it loads
    useEffect(() => {
        if (quoteProjectPurpose !== undefined) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- resynchronise le choix local quand le refresh serveur remplace le devis
            setProjectPurpose(projectPurposeKey(quoteProjectPurpose))
        }
    }, [quoteProjectPurpose])

    // SCPI is retired from the funnel but stays visible on the quotes that already carry it
    const projectPurposeOptions = useMemo(
        () =>
            PROJECT_PURPOSE_OPTIONS.filter(
                (option) => option.value !== 'scpi' || quoteProjectPurpose === PROJECT_PURPOSE_SCPI
            ).map((option) => ({
                ...option,
                badge:
                    option.value !== projectPurpose ? undefined : isProjectPurposeImported ? (
                        <LoanImportedBadge />
                    ) : loanTypeChatMark ? (
                        <LoanChatPrefilledBadge estimate={loanTypeChatMark.estimate} />
                    ) : undefined,
            })),
        [isProjectPurposeImported, loanTypeChatMark, projectPurpose, quoteProjectPurpose]
    )

    const handleProjectPurposeChange = (newPurpose: ProjectPurposeKey) => {
        setProjectPurpose(newPurpose)
        quoteActions.update({ project_purpose: projectPurposeId(newPurpose) })
    }

    // Ensure at least one loan exists in the store
    // This is needed so that form changes are synchronized with the store
    // Use a ref guard to prevent double-add in React Strict Mode (dev runs effects twice)
    const hasAddedInitialLoan = useRef(false)
    useEffect(() => {
        if (quote && (!quote.loans || quote.loans.length === 0) && !hasAddedInitialLoan.current) {
            hasAddedInitialLoan.current = true
            loanActions.add()
        }
    }, [quote, loanActions])

    // Déclenché au premier écran ≥ 2 : c'est le moment funnel « arrivée sur le formulaire »
    // d'aujourd'hui — le mount du wizard précède désormais le choix de projet (étape 1)
    const hasVisitedQuoteStepRef = useRef(false)
    useEffect(() => {
        if (!isServerDataLoaded || !quote?.token || hasVisitedQuoteStepRef.current) return
        if (screen < SCREEN_PROFILE_MAIN) return
        hasVisitedQuoteStepRef.current = true

        void updateQuoteStatus(quote.token, LOAN_STATUS.QUOTE_STEP_VISITED).catch((error) => {
            console.error('Failed to update quote status (QUOTE_STEP_VISITED):', error)
        })
    }, [isServerDataLoaded, quote?.token, screen])

    // Tous les écrans restent montés : un event de vue se déclenche sur le changement de `screen`,
    // pas au mount des composants d'étape. Le Set évite de regonfler les vues au retour arrière.
    const trackedScreensRef = useRef<Set<number>>(new Set())
    useEffect(() => {
        if (!isServerDataLoaded || !quote?.token || trackedScreensRef.current.has(screen)) return
        if (contactLocked && screen === SCREEN_CONTACT) return
        trackedScreensRef.current.add(screen)

        track({ event: AeTrackingEvents.formProgress, ...buildAeParams(quote), ...buildAeStepParams(screen) })
    }, [isServerDataLoaded, contactLocked, quote, track, screen])

    // Get loans for rendering
    const loans = useMemo(() => {
        return quote?.loans && quote.loans.length > 0 ? quote.loans : []
    }, [quote?.loans])

    // Check if can add more loans
    const canAddLoan = loans.length < MAX_LOANS && loans.length > 0

    // Insured capital = loans total × coverage share, compared to the insurers' ceiling
    const loansTotalAmount = useMemo(
        () =>
            computeLoansTotalAmount(
                quote?.loans,
                quote?.loans?.[0]?.new_date_loan || quote?.date_contract_begin || null,
                needsSwitching(quote?.type_quote)
            ),
        [quote?.loans, quote?.date_contract_begin, quote?.type_quote]
    )

    // Quotités lues dans le store : le champ y est synchronisé au blur, donc avant le clic sur Suivant
    const insuredAmountErrors = useMemo((): InsuredAmountErrors => {
        const mainCoverage = quote?.main_borrower?.proportion ?? 100
        const coCoverage = quote?.co_borrower?.activated ? (quote?.co_borrower?.proportion ?? 100) : null

        // Each borrower is checked against the ceiling, and each loan line against the bounds of its type
        const errorsForBounds = (bounds: ILoanFieldValidation['amount'] | null) => ({
            main: getInsuredAmountError({ loansTotal: loansTotalAmount, coverPercentage: mainCoverage, bounds }),
            co:
                coCoverage !== null
                    ? getInsuredAmountError({ loansTotal: loansTotalAmount, coverPercentage: coCoverage, bounds })
                    : null,
        })

        const perLoan: Record<number, string | null> = {}
        loans.forEach((loan) => {
            const { main, co } = errorsForBounds(resolveLoanBounds(globalOptions, loan.type_loan?.name)?.amount ?? null)
            perLoan[loan.id] = main ?? co
        })

        return { perLoan, ...errorsForBounds(resolveLoanBounds(globalOptions, null)?.amount ?? null) }
    }, [
        loans,
        globalOptions,
        loansTotalAmount,
        quote?.main_borrower?.proportion,
        quote?.co_borrower?.activated,
        quote?.co_borrower?.proportion,
    ])

    // Step 3 has no coverage share yet, so the floor is checked against the raw loans total
    const minInsuredAmountErrors = useMemo((): InsuredAmountErrors => {
        if (hasAltCurrencyLoan(loans)) return { perLoan: {}, main: null, co: null }

        const coCoverage = quote?.co_borrower?.activated ? (quote?.co_borrower?.proportion ?? 100) : null

        const perLoan: Record<number, string | null> = {}
        loans.forEach((loan) => {
            perLoan[loan.id] = getMinInsuredAmountError({
                loansTotal: loansTotalAmount,
                coverPercentage: 100,
                bounds: resolveLoanBounds(globalOptions, loan.type_loan?.name)?.amount ?? null,
                target: 'loan',
            })
        })

        const bounds = resolveLoanBounds(globalOptions, null)?.amount ?? null
        const coverageError = (coverPercentage: number) =>
            getMinInsuredAmountError({ loansTotal: loansTotalAmount, coverPercentage, bounds, target: 'coverage' })

        return {
            perLoan,
            main: coverageError(quote?.main_borrower?.proportion ?? 100),
            co: coCoverage !== null ? coverageError(coCoverage) : null,
        }
    }, [
        loans,
        globalOptions,
        loansTotalAmount,
        quote?.main_borrower?.proportion,
        quote?.co_borrower?.activated,
        quote?.co_borrower?.proportion,
    ])

    // The tooltip stays CSS-hidden until a step change is attempted, so an always-on floor would only redden fields
    const [revealedMinAmountAt, setRevealedMinAmountAt] = useState<string | null>(null)
    // Adding a loan line is how the total gets fixed, and a remount clears the shown errors: both drop the reveal
    const minAmountRevealKey = `${formsEpoch}-${loans.length}`
    const minAmountRevealed = revealedMinAmountAt === minAmountRevealKey
    const revealMinAmountError = () => setRevealedMinAmountAt(minAmountRevealKey)

    // Live validation de la quotité cumulée (main + co)
    const validateCoverageTotals = () => {
        // Only validate if there's a co-borrower
        if (!quote?.co_borrower?.activated) {
            setCoverageErrorState(null)
            return
        }

        const mainCoverage =
            mainGuaranteesFormRef.current?.getData()?.cover_percentage ?? quote?.main_borrower?.proportion ?? 0
        const coCoverage =
            coGuaranteesFormRef.current?.getData()?.cover_percentage ?? quote?.co_borrower?.proportion ?? 0
        const totalCoverage = mainCoverage + coCoverage

        if (totalCoverage < 100) {
            setCoverageErrorState(COVERAGE_ERROR_MESSAGE)
        } else {
            setCoverageErrorState(null)
        }
    }

    // Scroll to the first field with a validation error, matching Symfony behavior
    // Only the first error field shows its tooltip message; others just show red styling
    // Validate all forms
    const validateAllForms = async (): Promise<boolean> => {
        const validations: Promise<boolean>[] = []

        // Validate financing form
        if (financingFormRef.current) {
            validations.push(financingFormRef.current.validate())
        }

        // Validate all loan forms
        loanFormsRefs.current.forEach((formRef) => {
            if (formRef) {
                validations.push(formRef.validate())
            }
        })

        // Validate main borrower
        if (mainBorrowerFormRef.current) {
            validations.push(mainBorrowerFormRef.current.validate())
        }

        // Validate co-borrower if exists
        if (quote?.co_borrower?.activated && coBorrowerFormRef.current) {
            validations.push(coBorrowerFormRef.current.validate())
        }

        // Validate main borrower guarantees
        if (mainGuaranteesFormRef.current) {
            validations.push(mainGuaranteesFormRef.current.validate())
        }

        // Validate co-borrower guarantees if exists
        if (quote?.co_borrower?.activated && coGuaranteesFormRef.current) {
            validations.push(coGuaranteesFormRef.current.validate())
        }

        const results = await Promise.all(validations)
        return results.every((result) => result === true)
    }

    // Collect detailed validation errors from child forms
    const collectValidationErrors = (): string[] => {
        const errors: string[] = []

        if (financingFormRef.current?.getErrorMessages) {
            errors.push(...financingFormRef.current.getErrorMessages().map((msg) => `Financement: ${msg}`))
        }

        loans.forEach((loan, index) => {
            const ref = loanFormsRefs.current.get(loan.id)
            if (ref?.getErrorMessages) {
                errors.push(...ref.getErrorMessages().map((msg) => `Prêt ${index + 1}: ${msg}`))
            }
        })

        if (mainBorrowerFormRef.current?.getErrorMessages) {
            errors.push(...mainBorrowerFormRef.current.getErrorMessages().map((msg) => `Emprunteur principal: ${msg}`))
        }

        if (quote?.co_borrower?.activated && coBorrowerFormRef.current?.getErrorMessages) {
            errors.push(...coBorrowerFormRef.current.getErrorMessages().map((msg) => `Co-emprunteur: ${msg}`))
        }

        if (mainGuaranteesFormRef.current?.getErrorMessages) {
            errors.push(
                ...mainGuaranteesFormRef.current.getErrorMessages().map((msg) => `Garanties emprunteur: ${msg}`)
            )
        }

        if (quote?.co_borrower?.activated && coGuaranteesFormRef.current?.getErrorMessages) {
            errors.push(
                ...coGuaranteesFormRef.current.getErrorMessages().map((msg) => `Garanties co-emprunteur: ${msg}`)
            )
        }

        return errors
    }

    // Handle adding a new loan
    const handleAddLoan = () => {
        if (canAddLoan) {
            loanFormsRefs.current.forEach((formRef) => formRef.collapse())
            loanActions.add()
        }
    }

    // Handle removing a loan
    const handleRemoveLoan = (loanId: number) => {
        loanActions.remove(loanId)
        loanFormsRefs.current.delete(loanId)
    }

    // Set ref for a loan form
    const setLoanFormRef = (loanId: number, ref: LoanFormHandle | null) => {
        if (ref) {
            loanFormsRefs.current.set(loanId, ref)
        } else {
            loanFormsRefs.current.delete(loanId)
        }
    }

    // Prepare payload for API submission (matches Symfony backend format)
    // See: LoanComparatorAPIController.php -> updateQuoteFromParams(), updateBorrowerFromParams(), updateLoanFromParams()
    const preparePayload = useCallback(
        (guaranteesData: {
            mainCoverage: number
            mainOptions: Record<string, boolean>
            coCoverage: number | null
            coOptions: Record<string, boolean> | null
        }): IQuotationFormPayload => {
            // Get financing data from form ref (bank name, signature date)
            const financingData = financingFormRef.current?.getData()
            const firstLoan = quote?.loans?.[0]

            // Bank name and signature date come from financing form, with fallbacks
            const bankName = financingData?.bank_name || quote?.bank_name || firstLoan?.bank?.name || null
            const signatureDate =
                financingData?.signature_date || firstLoan?.new_date_loan || quote?.date_contract_begin || null
            const customBankName = financingData?.custom_bank_name || firstLoan?.custom_bank_name || null

            // Build loans array from form refs (not from store, as store might be empty)
            // Each loan form ref has a getData() method that returns the form values
            const loansFromForms: any[] = []
            loanFormsRefs.current.forEach((formRef, loanId) => {
                const loanFormData = formRef.getData()
                if (loanFormData) {
                    // Map form data to loan format expected by mapLoanForSubmission
                    loansFromForms.push({
                        id: loanId > 0 ? loanId : null, // Keep existing ID or null for new loans
                        sum: loanFormData.amount,
                        sum_left: loanFormData.amount_left,
                        period: loanFormData.duration,
                        month_left: loanFormData.duration_left,
                        rate: loanFormData.rate,
                        // Différé depuis la signature attendu par le back, même saisi « restant » en capital restant (RM-920)
                        time_offset: timeOffsetFromLoanForm(loanFormData, signatureDate),
                        partial_time_offset: loanFormData.delay_type === 'partial',
                        palier: loanFormData.stages,
                        information_type: loanFormData.save_amount_left
                            ? LOAN_INFORMATION_TYPES.SUM_LEFT
                            : LOAN_INFORMATION_TYPES.SUM_INITIAL,
                        selected_currency: loanFormData.selected_currency || 'euro',
                        type_loan: {
                            id: loanFormData.type,
                            name:
                                formOptions?.loanTypeList?.find(
                                    (lt: { id: number; name: string }) => lt.id === loanFormData.type
                                )?.name || 'Prêt classique',
                        },
                    })
                }
            })

            // Use loans from forms, or fallback to store if forms are empty
            const loansToSubmit = loansFromForms.length > 0 ? loansFromForms : quote?.loans || []
            const loansPayload = loansToSubmit.map((loan) => mapLoanForSubmission(loan)).filter(Boolean)

            // Build borrowers array - use coverage data passed as parameter
            const borrowers = []
            if (quote?.main_borrower) {
                const mainBorrowerWithCoverage = {
                    ...quote.main_borrower,
                    // Use cover_percentage from parameter
                    proportion: guaranteesData.mainCoverage,
                }
                borrowers.push(mapBorrowerForSubmission(mainBorrowerWithCoverage, 'main', guaranteesData.mainOptions))
            }
            if (quote?.co_borrower?.activated) {
                const coBorrowerWithCoverage = {
                    ...quote.co_borrower,
                    // Use cover_percentage from parameter
                    proportion: guaranteesData.coCoverage ?? quote.co_borrower.proportion ?? 100,
                }
                borrowers.push(mapBorrowerForSubmission(coBorrowerWithCoverage, 'co', guaranteesData.coOptions ?? {}))
            }

            return {
                borrowers: borrowers.filter(Boolean) as IQuotationFormPayload['borrowers'],
                loans: loansPayload.filter(Boolean) as IQuotationFormPayload['loans'],
                project_type: quote?.type_quote || 'newLoan',
                project_purpose: projectPurposeId(projectPurpose),
                is_loan_repurchase: quote?.is_loan_repurchase || false,
                external_delegation: quote?.delegation ?? true,
                current_insurance_company: quote?.current_insurance_company || null,
                selected_currency: loansPayload[0]?.selected_currency || 'euro',
                bank: {
                    name: bankName,
                    custom_bank_name: customBankName,
                    signature_date: signatureDate,
                    required_guarantees: [],
                    loan_was_renegotiated: firstLoan?.had_loan_renegotiation || false,
                    renegotiation_date: firstLoan?.renegotiation_date || null,
                    address: {
                        street: null,
                        additional_address: null,
                        zip_code: null,
                        city: null,
                        country: 'FRANCE',
                    },
                    agency: null,
                },
                abtest: {
                    use_prospect_route_in_results: true,
                },
                is_first_quotation: !quote?.token, // True if no token exists yet
            }
        },
        [quote, projectPurpose, formOptions]
    )

    // Handle quotation submission (Suivant de l'écran assurance) — corps identique à l'ancien submit
    const submitQuotation = async () => {
        // Clear previous errors
        uiActions.clearAllErrors()
        setIsSubmitting(true)

        try {
            // Get coverage data from the forms
            const mainFormData = mainGuaranteesFormRef.current?.getData()
            const coFormData = coGuaranteesFormRef.current?.getData()

            const mainCoverage = mainFormData?.cover_percentage ?? quote?.main_borrower?.proportion ?? 100
            const coCoverage = quote?.co_borrower?.activated
                ? (coFormData?.cover_percentage ?? quote?.co_borrower?.proportion ?? 100)
                : null

            // Vérif immédiate de la quotité cumulée avant toute autre validation (only if co-borrower)
            if (quote?.co_borrower?.activated && coCoverage !== null) {
                const totalCoverage = mainCoverage + coCoverage
                if (totalCoverage < 100) {
                    setCoverageErrorState(COVERAGE_ERROR_MESSAGE)
                    setIsSubmitting(false)
                    scrollToFirstErrorInScreen(SCREEN_INSURANCE)
                    return
                } else {
                    setCoverageErrorState(null)
                }
            }

            if (insuredAmountErrors.main || insuredAmountErrors.co) {
                setIsSubmitting(false)
                scrollToFirstErrorInScreen(SCREEN_INSURANCE)
                return
            }

            if (minInsuredAmountErrors.main || minInsuredAmountErrors.co) {
                revealMinAmountError()
                setIsSubmitting(false)
                scrollToFirstErrorInScreen(SCREEN_INSURANCE)
                return
            }

            // Validate all forms
            const isValid = await validateAllForms()

            if (!isValid) {
                const detailedErrors = collectValidationErrors()
                const message = detailedErrors.length
                    ? `Veuillez corriger les erreurs suivantes :\n- ${detailedErrors.join('\n- ')}`
                    : 'Veuillez corriger les erreurs dans le formulaire.'
                uiActions.setError('form', message)
                setIsSubmitting(false)
                goToFirstScreenWithError(QUOTATION_ERROR_SCREENS_ORDER, screen, goToScreen)
                return
            }

            // Get options from the guarantees forms
            const mainOptions = mainFormData?.options ?? {
                DC: false,
                PTIA: false,
                IPT_ITT: false,
                IPP: false,
                PLUS: false,
            }
            const coOptions = coFormData?.options ?? null

            // Prepare and submit the payload to the API
            const payload = preparePayload({ mainCoverage, mainOptions, coCoverage, coOptions })
            const existingToken = quote?.token

            const response = await postQuotationForm(payload, existingToken)
            const newToken = response.token

            // Update the store with the new token if it's a new quote
            if (!existingToken && newToken) {
                quoteActions.update({ token: newToken })
            }

            // Même sémantique que le seed SSR de la page coordonnées d'avant : ids et champs
            // dérivés serveur frais avant l'étape coordonnées
            const refreshedQuote = await getLoanQuoteFromToken(newToken || existingToken || '')
            quoteActions.setQuote(refreshedQuote)

            // Update step for UI
            uiActions.setCurrentStep(2)

            setIsSubmitting(false)
            goToScreen(SCREEN_CONTACT)
        } catch (error) {
            console.error('Error submitting form:', error)
            // If the error is a validation error (ZodError), treat it as a validation failure
            const isValidationError = error && typeof error === 'object' && 'issues' in error
            const errorMessage = isValidationError
                ? 'Veuillez corriger les erreurs dans le formulaire.'
                : error instanceof Error
                  ? error.message
                  : 'Une erreur est survenue lors de la soumission du formulaire.'
            uiActions.setError('form', errorMessage)
            scrollToFirstErrorInScreen(SCREEN_INSURANCE)
            setIsSubmitting(false)
        }
    }

    // Suivant de l'étape 1 : validation des 2 réponses, puis POST projet + refresh serveur
    // uniquement si le type de projet a changé (séquence « parcours POST → seed SSR » d'avant)
    const handleProjectNext = async () => {
        const currentQuote = useLoanComparatorStore.getState().quote
        const pathValue = deriveProjectPathValue(currentQuote)
        const borrowersAnswered =
            currentQuote?.co_borrower?.activated === true || currentQuote?.co_borrower?.activated === false

        if (!pathValue || !borrowersAnswered) {
            setProjectError('Veuillez répondre à cette question.')
            scrollToFirstErrorInScreen(SCREEN_PROJECT)
            return
        }
        setProjectError(null)

        if (!currentQuote?.token) return

        if (postedPathRef.current === pathValue) {
            goToScreen(SCREEN_PROFILE_MAIN)
            return
        }

        setIsNavigating(true)
        try {
            const projectTypeValue = pathValue === 'switching' ? PROJECT_TYPE_SWITCHING : PROJECT_TYPE_NEW_LOAN
            await postProjectType(currentQuote.token, projectTypeValue, false)

            // type_quote (dérivé serveur) pilote libellés, bornes et schéma zod du financement
            const activatedAnswer = currentQuote.co_borrower?.activated
            const refreshedQuote = await getLoanQuoteFromToken(currentQuote.token)
            quoteActions.setQuote(refreshedQuote)

            // Le refresh écrase la réponse Seul/À deux (locale tant que rien n'est posté) : on la ré-applique
            if (activatedAnswer === true) {
                borrowerActions.addCoBorrower()
            } else {
                borrowerActions.deactivateCoBorrower()
            }

            // Le quote serveur peut revenir sans prêt : réarme l'effet qui garantit une ligne initiale
            hasAddedInitialLoan.current = false

            postedPathRef.current = pathValue
            setFormsEpoch((epoch) => epoch + 1)
            goToScreen(SCREEN_PROFILE_MAIN)
        } catch (error) {
            console.error('Error posting project type:', error)
            // Rien ne rend uiErrors.quote sur l'écran 1 : sans ça l'échec est muet, le loader s'arrête et l'écran ne bouge pas
            setProjectError('Une erreur est survenue, merci de réessayer.')
        } finally {
            setIsNavigating(false)
        }
    }

    const handleNext = async () => {
        switch (screen) {
            case SCREEN_PROJECT:
                await handleProjectNext()
                break

            case SCREEN_PROFILE_MAIN: {
                const isValid = await mainBorrowerFormRef.current?.validate()
                if (!isValid) {
                    scrollToFirstErrorInScreen(SCREEN_PROFILE_MAIN)
                    return
                }
                goToScreen(hasCoBorrower ? SCREEN_PROFILE_CO : SCREEN_LOAN)
                break
            }

            case SCREEN_PROFILE_CO: {
                const isValid = await coBorrowerFormRef.current?.validate()
                if (!isValid) {
                    scrollToFirstErrorInScreen(SCREEN_PROFILE_CO)
                    return
                }
                goToScreen(SCREEN_LOAN)
                break
            }

            case SCREEN_LOAN: {
                const validations: Promise<boolean>[] = []
                if (financingFormRef.current) {
                    validations.push(financingFormRef.current.validate())
                }
                loanFormsRefs.current.forEach((formRef) => {
                    if (formRef) {
                        validations.push(formRef.validate())
                    }
                })
                const results = await Promise.all(validations)
                let isValid = results.every((result) => result === true)

                if (showBankCoverageQuestion && !isBankCoverageAnswerComplete(bankCoverageAnswer, quote)) {
                    setBankCoverageError('Veuillez répondre à cette question.')
                    isValid = false
                } else {
                    setBankCoverageError(null)
                }

                if (Object.values(insuredAmountErrors.perLoan).some(Boolean)) {
                    isValid = false
                }

                if (Object.values(minInsuredAmountErrors.perLoan).some(Boolean)) {
                    revealMinAmountError()
                    isValid = false
                }

                if (!isValid) {
                    scrollToFirstErrorInScreen(SCREEN_LOAN)
                    return
                }
                goToScreen(SCREEN_INSURANCE)
                break
            }

            case SCREEN_INSURANCE:
                await submitQuotation()
                break
        }
    }

    const handleBack = () => {
        switch (screen) {
            case SCREEN_PROFILE_MAIN:
                goToScreen(SCREEN_PROJECT)
                break
            case SCREEN_PROFILE_CO:
                goToScreen(SCREEN_PROFILE_MAIN)
                break
            case SCREEN_LOAN:
                goToScreen(hasCoBorrower ? SCREEN_PROFILE_CO : SCREEN_PROFILE_MAIN)
                break
            case SCREEN_INSURANCE:
                goToScreen(SCREEN_LOAN)
                break
            case SCREEN_CONTACT:
                goToScreen(SCREEN_INSURANCE)
                break
        }
    }

    // Wait for hydration AND server data before rendering form
    // This ensures we don't render with stale localStorage data
    if (!hasHydrated || !isServerDataLoaded) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement du formulaire...</p>
                </div>
            </div>
        )
    }

    const navFooter = (
        <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center md:justify-between">
            <AppButtonNew
                type="button"
                theme="outline-rounded"
                icon="fa-chevron-left"
                iconType="regular"
                onClick={handleBack}
                disabled={screen === SCREEN_PROJECT || isSubmitting || isNavigating}
                dataTest="previous-button"
            >
                Précédent
            </AppButtonNew>

            <AppButtonNew
                type="button"
                theme="orange-rounded"
                onClick={handleNext}
                icon={isSubmitting || isNavigating || isExtracting ? 'fa-spinner fa-spin' : 'fa-chevron-right'}
                iconPosition="after"
                iconType="regular"
                disabled={
                    isNavigating ||
                    isExtracting ||
                    (screen === SCREEN_LOAN && isAwaitingImport) ||
                    (isSubmitScreen && isSubmitting)
                }
                dataTest={isSubmitScreen ? 'loan-comparator-quotation-form-submit' : 'next-button'}
            >
                {isExtracting
                    ? 'Analyse en cours…'
                    : screen === SCREEN_LOAN && isVerifyingImport
                      ? "C'est exact, continuer"
                      : 'Suivant'}
            </AppButtonNew>
        </div>
    )

    return (
        <>
            {/* Bandeau d'étapes : barre segmentée + « Étape X sur 5 » + titre de l'étape.
                Cliquable en arrière uniquement : revenir ne re-valide pas (même sémantique que Précédent) */}
            <div className="mb-4 md:mb-6">
                <StepsBreadcrumb
                    steps={buildLoanQuotationBreadcrumbs(screen)}
                    currentStep={screen}
                    onStepClick={goToScreen}
                    isStepClickable={(step) => step < screen && !isExtracting}
                />
            </div>
            {isLoanPromoCodeEnabled() && screen !== SCREEN_CONTACT && <LoanPromoStepReminder />}

            {/* preventDefault : la navigation et la soumission passent par les boutons Précédent/Suivant */}
            <LoanPdfImportProvider value={pdfImportValue}>
                <form onSubmit={(e) => e.preventDefault()}>
                    {/* Écran 1 : Type de projet */}
                    <div data-loan-screen={SCREEN_PROJECT} className={screen === SCREEN_PROJECT ? '' : 'hidden'}>
                        <LoanComparatorProjectStep
                            error={projectError}
                            onAnswerChange={() => setProjectError(null)}
                            footer={screen === SCREEN_PROJECT ? navFooter : null}
                        />
                    </div>

                    {/* Écran 2 : Profil emprunteur */}
                    <div
                        data-loan-screen={SCREEN_PROFILE_MAIN}
                        className={screen === SCREEN_PROFILE_MAIN ? '' : 'hidden'}
                    >
                        <section>
                            <div className="mt-6">
                                <ShadowBox flatOnMobile className="mb-5 md:py-5 md:px-5">
                                    <h2
                                        style={{ fontFamily: 'var(--font-recoleta)' }}
                                        className="block text-3xl md:text-4xl font-bold text-pale mt-0 mb-2.5"
                                    >
                                        Profil emprunteur
                                    </h2>
                                    <p className="text-[15px] text-pale/80 mb-4 md:mb-8">
                                        Renseignez les caractéristiques de votre prêt pour obtenir une estimation
                                        précise.
                                    </p>
                                    <LoanComparatorQuotationBorrowerForm
                                        key={`main-borrower-${formsEpoch}`}
                                        ref={mainBorrowerFormRef}
                                        isMainBorrower={true}
                                    />
                                    {screen === SCREEN_PROFILE_MAIN && <div className="mt-4 md:mt-8">{navFooter}</div>}
                                </ShadowBox>
                            </div>
                        </section>
                    </div>

                    {/* Écran 2bis : Profil co-emprunteur (même étape visible, titre du bandeau permuté) */}
                    {hasCoBorrower && (
                        <div
                            data-loan-screen={SCREEN_PROFILE_CO}
                            className={screen === SCREEN_PROFILE_CO ? '' : 'hidden'}
                        >
                            <section>
                                <div className="mt-6">
                                    <ShadowBox flatOnMobile className="mb-5 md:py-5 md:px-5">
                                        <h2
                                            style={{ fontFamily: 'var(--font-recoleta)' }}
                                            className="block text-3xl md:text-4xl font-bold text-pale mt-0 mb-2.5"
                                        >
                                            Profil co-emprunteur
                                        </h2>
                                        <p className="text-[15px] text-pale/80 mb-4 md:mb-8">
                                            Renseignez les caractéristiques de votre prêt pour obtenir une estimation
                                            précise.
                                        </p>
                                        <LoanComparatorQuotationBorrowerForm
                                            key={`co-borrower-${formsEpoch}`}
                                            ref={coBorrowerFormRef}
                                            isMainBorrower={false}
                                        />
                                        {screen === SCREEN_PROFILE_CO && (
                                            <div className="mt-4 md:mt-8">{navFooter}</div>
                                        )}
                                    </ShadowBox>
                                </div>
                            </section>
                        </div>
                    )}

                    {/* Écran 3 : Caractéristiques de votre emprunt */}
                    <div data-loan-screen={SCREEN_LOAN} className={screen === SCREEN_LOAN ? '' : 'hidden'}>
                        <section>
                            <ShadowBox flatOnMobile className="mt-6 mb-5 md:py-5 md:px-5">
                                <h2
                                    style={{ fontFamily: 'var(--font-recoleta)' }}
                                    className="block text-3xl md:text-4xl font-bold text-pale mt-0 mb-2.5"
                                >
                                    {isVerifyingImport
                                        ? 'Vérifiez ce que nous avons lu'
                                        : 'Caractéristiques de votre emprunt'}
                                </h2>
                                {isVerifyingImport ? (
                                    <p className="text-[15px] text-pale/80 mb-4 md:mb-6">
                                        Tout est modifiable. Corrigez ce qui ne correspond pas à votre offre avant de
                                        continuer.
                                    </p>
                                ) : isGuidedLoanStep ? (
                                    !isSignatureDateValid && (
                                        <p className="text-[15px] text-pale/80 mb-4 md:mb-6">
                                            Deux informations suffisent pour démarrer. Nous pouvons ensuite remplir le
                                            reste à partir de votre offre de prêt.
                                        </p>
                                    )
                                ) : (
                                    <>
                                        <p className="text-[15px] text-pale/80 mb-2">
                                            Renseignez les caractéristiques de votre prêt pour obtenir une estimation
                                            précise.
                                        </p>
                                        <p className="text-[15px] text-pale/80 mb-4 md:mb-8">
                                            Un projet peut comporter plusieurs lignes de prêt ; si vous avez plusieurs
                                            projets, contactez{' '}
                                            <a href="mailto:conseil@reassurez-moi.fr" className="underline">
                                                conseil@reassurez-moi.fr
                                            </a>
                                            .
                                        </p>
                                    </>
                                )}

                                {/* Question assureur du parcours (changement d'assurance uniquement) */}
                                {showBankCoverageQuestion && (
                                    <LoanComparatorBankCoverageQuestion
                                        value={bankCoverageAnswer}
                                        onChange={setBankCoverageAnswer}
                                        error={bankCoverageError}
                                        isActive={screen === SCREEN_LOAN}
                                    />
                                )}

                                <LoanComparatorFinancingForm
                                    key={`financing-${formsEpoch}`}
                                    ref={financingFormRef}
                                    className={isGuidedLoanStep ? '' : 'mb-6'}
                                    layout={isGuidedLoanStep ? 'guided' : 'legacy'}
                                    signatureLocked={isExtracting}
                                    bankHidden={isAwaitingImport}
                                    bankSubLabel={
                                        isVerifyingImport
                                            ? "Lue sur l'en-tête de votre offre"
                                            : 'Celle qui vous accorde le prêt'
                                    }
                                    onSignatureDateChange={isGuidedLoanStep ? handleSignatureDateChange : undefined}
                                    sessionSignatureDate={isGuidedLoanStep ? signatureDate : undefined}
                                    betweenSections={
                                        isGuidedLoanStep ? (
                                            <LoanFillModeSelector
                                                mode={fillMode}
                                                onModeChange={requestFillMode}
                                                cardsHidden={isVerifyingImport}
                                            >
                                                {fillMode === 'import' && (
                                                    <LoanPdfExtractBlock
                                                        extract={pdfExtract}
                                                        locked={!isSignatureDateValid}
                                                        signatureDate={signatureDate}
                                                        importedCount={pdfImportValue.importedFields.size}
                                                        onFileAccepted={handleFileAccepted}
                                                        onFileRejected={pdfExtract.fail}
                                                        onManualEntry={() => requestFillMode('manual')}
                                                        onHelpOpen={handleHelpOpen}
                                                    />
                                                )}
                                            </LoanFillModeSelector>
                                        ) : undefined
                                    }
                                />

                                {isExtracting && <LoanPdfExtractSkeletons />}

                                <div className={isAwaitingImport ? 'hidden' : ''} aria-hidden={isAwaitingImport}>
                                    {loans.map((loan, index) => (
                                        <LoanComparatorLoanForm
                                            key={`loan-${loan.id}-${index}-${formsEpoch}`}
                                            loanId={loan.id}
                                            loanIndex={index}
                                            ref={(ref) => setLoanFormRef(loan.id, ref)}
                                            canRemove={loans.length > 1}
                                            onRemove={() => handleRemoveLoan(loan.id)}
                                            isLast={index === loans.length - 1}
                                            externalAmountError={
                                                insuredAmountErrors.perLoan[loan.id] ??
                                                (minAmountRevealed ? minInsuredAmountErrors.perLoan[loan.id] : null)
                                            }
                                        />
                                    ))}

                                    {canAddLoan && (
                                        <DashedAddCard
                                            label="Je souhaite ajouter une ligne de prêt"
                                            subtitle="Prêt relais, PTZ, prêt employeur… chaque ligne est assurée indépendamment."
                                            onClick={handleAddLoan}
                                        />
                                    )}
                                </div>

                                {screen === SCREEN_LOAN && <div className="mt-4 md:mt-8">{navFooter}</div>}
                            </ShadowBox>
                        </section>
                    </div>

                    {/* Écran 4 : Votre assurance — emprunteur et co-emprunteur côte à côte (maquette) */}
                    <div data-loan-screen={SCREEN_INSURANCE} className={screen === SCREEN_INSURANCE ? '' : 'hidden'}>
                        <section>
                            <div className="mt-6">
                                <ShadowBox flatOnMobile className="mb-5 md:py-5 md:px-5">
                                    <h2
                                        style={{ fontFamily: 'var(--font-recoleta)' }}
                                        className="block text-3xl md:text-4xl font-bold text-pale mt-0 mb-4 md:mb-8"
                                    >
                                        Votre assurance
                                    </h2>

                                    {uiErrors?.coverage && (
                                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700">
                                            {uiErrors.coverage}
                                        </div>
                                    )}

                                    <div className="mb-6">
                                        <LoanFormField
                                            label="Quel est votre projet immobilier ?*"
                                            tooltip={{
                                                title: 'Type de projet',
                                                text: "Si vous avez plusieurs prêts pour différents projets (résidence principale/secondaire, investissement locatif ...) nous vous conseillons d'effectuer une simulation par projet afin d'avoir un devis plus précis et nous permettre de mieux vous conseiller",
                                            }}
                                            tooltipId="project-type-tooltip"
                                        >
                                            <DescriptiveToggle
                                                className={isProjectPurposeImported ? 'loan-field-imported' : ''}
                                                options={projectPurposeOptions}
                                                value={projectPurpose}
                                                onChange={handleProjectPurposeChange}
                                                columns={4}
                                                mobileColumns={2}
                                                hideDescriptionOnMobile
                                                ariaLabel="Type de projet"
                                            />
                                        </LoanFormField>
                                    </div>

                                    <div
                                        className={
                                            hasCoBorrower ? 'grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-6' : ''
                                        }
                                    >
                                        <div>
                                            {hasCoBorrower && (
                                                <h3
                                                    style={{ fontFamily: 'var(--font-recoleta)' }}
                                                    className="text-2xl font-bold text-pale mt-0 mb-4"
                                                >
                                                    Emprunteur
                                                </h3>
                                            )}
                                            <LoanComparatorBorrowerGuaranteesForm
                                                key={`main-guarantees-${formsEpoch}`}
                                                ref={mainGuaranteesFormRef}
                                                isMainBorrower={true}
                                                onCoverageBlur={validateCoverageTotals}
                                                externalCoverageError={
                                                    coverageError ??
                                                    insuredAmountErrors.main ??
                                                    (minAmountRevealed ? minInsuredAmountErrors.main : null)
                                                }
                                            />
                                        </div>
                                        {hasCoBorrower && (
                                            <div>
                                                <h3
                                                    style={{ fontFamily: 'var(--font-recoleta)' }}
                                                    className="text-2xl font-bold text-pale mt-0 mb-4"
                                                >
                                                    Co-emprunteur
                                                </h3>
                                                <LoanComparatorBorrowerGuaranteesForm
                                                    key={`co-guarantees-${formsEpoch}`}
                                                    ref={coGuaranteesFormRef}
                                                    isMainBorrower={false}
                                                    externalCoverageError={
                                                        coverageError ??
                                                        insuredAmountErrors.co ??
                                                        (minAmountRevealed ? minInsuredAmountErrors.co : null)
                                                    }
                                                    onCoverageBlur={validateCoverageTotals}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {screen === SCREEN_INSURANCE && <div className="mt-4 md:mt-8">{navFooter}</div>}
                                </ShadowBox>
                            </div>
                        </section>
                    </div>

                </form>
                {pendingFillMode && (
                    <LoanFillModeConfirmModal
                        isAnalyzing={pdfExtract.status === 'analyzing'}
                        onConfirm={() => applyFillMode(pendingFillMode)}
                        onCancel={() => setPendingFillMode(null)}
                    />
                )}
            </LoanPdfImportProvider>

            {/* Écran 5 : Vos coordonnées — hors du <form> (le form identité a sa propre balise form) */}
            {hasReachedContact && !contactLocked && (
                <div data-loan-screen={SCREEN_CONTACT} className={screen === SCREEN_CONTACT ? '' : 'hidden'}>
                    <ShadowBox flatOnMobile className="mb-5 md:p-6">
                        <LoanComparatorIdentityForm headingTag="h2" onBack={() => goToScreen(SCREEN_INSURANCE)} />
                    </ShadowBox>
                </div>
            )}
        </>
    )
}

// Dashed "add" card: light blue dashed border, left-aligned blue plus + label + italic subtitle
function DashedAddCard({
    label,
    subtitle,
    onClick,
    className = '',
}: {
    label: string
    subtitle?: string
    onClick?: () => void
    className?: string
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full rounded-2xl bg-[#F4F8FF] px-4 py-3 flex items-center gap-3 text-left cursor-pointer hover:bg-[#EAF1FF] transition-colors ${className}`}
            style={{
                backgroundImage:
                    "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='16' ry='16' stroke='%237196FF88' stroke-width='2' stroke-dasharray='6px%2c 8px' stroke-dashoffset='3' stroke-linecap='square'/%3e%3c/svg%3e\")",
            }}
        >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rm-blue">
                <i className="fa-regular fa-plus text-white text-[13px]" />
            </span>
            <span className="flex flex-col">
                <span className="text-[15px] text-pale">{label}</span>
                {subtitle && <span className="text-[12px] italic text-pale/80">{subtitle}</span>}
            </span>
        </button>
    )
}
